// vim: set fdm=marker :
// {{{ import
import { useRef, useEffect, useReducer, useCallback } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { format, startOfDay, endOfDay, addDays, subDays } from 'date-fns';

import { setState } from '@chronos/ui-toolkit/actions';
import { delay } from '@chronos/utils';
import keyValueReducer from '@chronos/ui-toolkit/reducers/keyValueReducer';
import { jsonApiNormalizer } from '@chronos/ui-toolkit/jsonapiNormalizer';
import log from '@chronos/log';
import { chronosApi } from 'api';
// }}}

const itemsPerPage = 40;
// {{{ initialState{}
const initialState = {
  isLoading: true,
  isInitialized: true,
  isPageLoading: {},
  isPageLoaded: {},

  filter: {
    showEUVAT: null,
    showEUWithoutVAT: null,
    showNotEU: null,
    date: {
      minDate: startOfDay(new Date(2018, 0, 1)),
      maxDate: endOfDay(new Date()),
      ranges: [
        {
          startDate: startOfDay(subDays(new Date(), 31)),
          endDate: endOfDay(new Date()),
          key: 'selection',
        },
      ],
    },
  },

  totalIncome: {},
  transactions: {},
  workspaces: {},

  // sorted map of fetched transactions
  // key is index in the list, value is id of transaction
  listItems: {},

  // Default non zero count needs to display placeholders
  transactionsCount: itemsPerPage,

  dayPoints: {},
};
// }}}

const useTransactionsList = () => {
  const [state, dispatch] = useReducer(keyValueReducer, {
    ...initialState,
  });
  const listRef = useRef(null);
  const infiniteLoaderRef = useRef(null);
  const autosizerRef = useRef(null);
  const isFirstRunForFiltersCheck = useRef(true);

  // {{{ 🔋 reloadList
  function reloadList() {
    log.debug('🔋', 'reloadList');
    dispatch(
      setState({
        isPageLoaded: {},
      })
    );
    if (listRef.current?.resetAfterIndex) {
      listRef.current.resetAfterIndex(0, true);
    }
    setTimeout(() => {
      if (infiniteLoaderRef.current) {
        log.debug('resetloadMoreItemsCache');
        infiniteLoaderRef.current.resetloadMoreItemsCache(true);
      }
    }, 100);
  }
  // }}}

  // {{{ clear list
  function clearList() {
    dispatch(
      setState({
        listItems: {},
      })
    );
    reloadList();
  }
  // }}}

  // {{{ 🩸 fetchTransactions
  const fetchTransactions = useDebouncedCallback(
    useCallback(
      async (startIndex = 0, stopIndex = itemsPerPage) => {
        log.debug('[fetchTransactions]:', 'run attempt');
        log.debug('[fetchTransactions]:', {
          startIndex,
          stopIndex,
        });
        const startPage = Math.floor(startIndex / itemsPerPage);
        const stopPage = Math.floor(stopIndex / itemsPerPage);
        const shouldSkip =
          state.isPageLoading[startPage] === true ||
          state.isPageLoading[stopPage] === true ||
          state.isPageLoaded[startPage] === true ||
          state.isPageLoaded[stopPage] === true;

        if (shouldSkip) {
          log.debug('[fetchTransactions]:', 'skip', {
            startPage,
            stopPage,
            isPageLoaded: state.isPageLoaded,
            isPageLoading: state.isPageLoading,
          });
          return null;
        }

        try {
          log.debug('[fetchTransactions]:', '🩸', 'page fetching...', {
            startPage,
            stopPage,
          });
          dispatch(
            setState({
              isPageLoading: {
                [startPage]: true,
                [stopPage]: true,
                _merge: true,
              },
            })
          );
          const isSamePage = startPage === stopPage;
          // If a list view see a border of two pages limit should be extended to fetch 2 pages
          const queryLimit = isSamePage ? itemsPerPage : itemsPerPage * 2;
          const offset = startPage * itemsPerPage;

          log.debug('[fetchTransactions]:', {
            queryLimit,
            offset,
          });
          const response = await chronosApi.getPaymentTransactions({
            body: {
              timezoneOffset: new Date().getTimezoneOffset(),
              limit: queryLimit,
              offset,
              showEUVAT: state.filter.showEUVAT,
              showEUWithoutVAT: state.filter.showEUWithoutVAT,
              showNotEU: state.filter.showNotEU,
              startDate: startOfDay(
                state.filter.date.ranges[0].startDate
              ).toISOString(),
              endDate: endOfDay(
                state.filter.date.ranges[0].endDate
              ).toISOString(),
            },
          });
          const normalizedData = jsonApiNormalizer(response);

          const { listItems, transactions } = normalizedData.resources.reduce(
            (acc, transaction, index) => {
              const createdAt = new Date(transaction.attributes.createdAt);
              const finishedAt = addDays(
                createdAt,
                transaction.attributes.data.periodDays
              );
              acc.transactions[transaction.id] = {
                ...transaction,
                computed: {
                  createdAt,
                  finishedAt,
                  createdAtTimeStr: format(createdAt, 'kk:mm'),
                  createdAtDayStr: format(createdAt, 'y-MM-dd'),
                  finishedAtDayStr: format(finishedAt, 'y-MM-dd'),
                },
              };
              acc.listItems[index + offset] = transaction.id;
              return acc;
            },
            {
              transactions: {},
              listItems: {},
            }
          );

          dispatch(
            setState({
              transactions: {
                ...transactions,
                _merge: true,
              },
              workspaces: {
                ...(normalizedData.includedResources?.workspaces ?? {}),
                _merge: true,
              },
              listItems: {
                ...listItems,
                _merge: true,
              },
              isPageLoading: {
                [startPage]: false,
                [stopPage]: false,
                _merge: true,
              },
              isPageLoaded: {
                [startPage]: true,
                [stopPage]: true,
                _merge: true,
              },
            })
          );
          if (listRef.current?.resetAfterIndex) {
            listRef.current.resetAfterIndex(0, true);
          }
        } catch (err) {
          log.error(err);
          dispatch(
            setState({
              isPageLoading: {
                [startPage]: false,
                [stopPage]: false,
                _merge: true,
              },
              isPageLoaded: {
                [startPage]: false,
                [stopPage]: false,
                _merge: true,
              },
            })
          );
        }
        return null;
      },
      [state.isPageLoading, state.isPageLoaded, state.filter]
    ),
    1000,
    {
      leading: true,
    }
  );
  // }}}

  // {{{  👁 fetchTransactionsMeta
  const fetchTransactionsMeta = useCallback(
    async (params) => {
      const { shouldMarkAsLoading = false, shouldReloadListIfChange = true } =
        params ?? {};
      log.debug('👁', '[uwl]:[fwm]', {
        shouldMarkAsLoading,
        shouldReloadListIfChange,
      });
      if (shouldMarkAsLoading) {
        dispatch(
          setState({
            isLoading: true,
          })
        );
      }

      try {
        const { totalIncome, totalTransactions, dayPoints } =
          await chronosApi.getPaymentTransactions({
            body: {
              meta: true,
              showEUVAT: state.filter.showEUVAT,
              showEUWithoutVAT: state.filter.showEUWithoutVAT,
              showNotEU: state.filter.showNotEU,
              startDate: startOfDay(
                state.filter.date.ranges[0].startDate
              ).toISOString(),
              endDate: endOfDay(
                state.filter.date.ranges[0].endDate
              ).toISOString(),
              timezoneOffset: new Date().getTimezoneOffset(),
            },
          });
        const currentTotalTransactions = state.transactionsCount;

        const isTotalIncomeChanged = Object.keys(totalIncome).some(
          (currency) => state.totalIncome[currency] !== totalIncome[currency]
        );
        const isTotalTransactionsCountChanged =
          currentTotalTransactions === 0 ||
          currentTotalTransactions !== totalTransactions;

        if (
          shouldReloadListIfChange &&
          (isTotalIncomeChanged || isTotalTransactionsCountChanged)
        ) {
          log.debug('👁', '[uwl]:[fwm]', 'reloadList(🔋)', {
            isTotalIncomeChanged,
            isTotalTransactionsCountChanged,
          });
          reloadList();
        } else {
          log.debug('[uwl]:[fwm]', 'skip reloadList', {
            isTotalIncomeChanged,
            isTotalTransactionsCountChanged,
          });
        }

        dispatch(
          setState({
            totalIncome,
            dayPoints,
            transactionsCount: totalTransactions,
          })
        );
      } catch (err) {
        log.error(err);
      }
      if (shouldMarkAsLoading) {
        dispatch(
          setState({
            isLoading: false,
          })
        );
      }
    },
    [state.filter, infiniteLoaderRef.current, state.transactionsCount]
  );
  // }}}

  // {{{ 🕹️ onChangeFilterEffect
  const onChangeFilterEffect = useDebouncedCallback(
    useCallback(async () => {
      log.debug('🕹️', '[uwl]:[ocf]:', 'onChangeFilter', state.filter);

      if (!isFirstRunForFiltersCheck.current && state.isInitialized) {
        const reloadState = {
          transactionsCount: itemsPerPage + 5,
          listItems: {},
          isPageLoaded: {},
        };
        dispatch(setState(reloadState));

        log.debug('🕹️', '[uwl]:[ocf]:', 'reload!', reloadState);
        log.debug('🕹️', '[uwl]:[ocf]:', 'run fetchWorklogsMeta(👁)');
        await fetchTransactionsMeta({
          shouldMarkAsLoading: false,
          shouldReloadListIfChange: false,
        });
        log.debug('🕹️', '[uwl]:[ocf]:', 'run reloadList(🔋)');
        reloadList();
      }
      if (state.isInitialized) {
        isFirstRunForFiltersCheck.current = false;
      }
    }, [fetchTransactionsMeta, fetchTransactions, state.isPageLoaded]),
    1000,
    {
      leading: true,
    }
  );
  useEffect(() => {
    onChangeFilterEffect();
  }, [state.isInitialized, state.filter]);
  // }}}

  // {{{ onChangeFilter
  const onChangeFilter = useCallback(
    (filterName, selected) => {
      switch (filterName) {
        case 'showEUVAT':
        case 'showEUWithoutVAT':
        case 'showNotEU': {
          return dispatch(
            setState('filter', {
              [filterName]: selected,
              _merge: true,
            })
          );
        }
        case 'date':
          return dispatch(
            setState('filter', {
              date: {
                ranges: [selected],
                _merge: true,
              },
            })
          );
        default:
          return dispatch(
            setState({
              filter: {
                [filterName]: {
                  selected,
                  _merge: true,
                },
                _merge: true,
              },
            })
          );
      }
    },
    [state.filter]
  );
  // }}}

  // {{{ onResize
  /* reset after resize */
  // {{
  const resetAfterResize = useDebouncedCallback(
    () => {
      if (listRef.current?.resetAfterIndex) {
        listRef.current.resetAfterIndex(0, true);
      }
      /* eslint-disable */
      if (autosizerRef.current?._onResize) {
        autosizerRef.current?._onResize();
      }
      /* eslint-enable */
    },
    100,
    {
      leading: true,
    }
  );
  useEffect(() => {
    window.addEventListener('resize', resetAfterResize);
    return () => {
      window.removeEventListener('resize', resetAfterResize);
    };
  }, []);
  // }}}

  // {{{ isItemLoaded
  const isItemLoaded = (index) => {
    let isLoaded = !state.isLoading;

    if (isLoaded) {
      const page = Math.floor(index / itemsPerPage);
      isLoaded = state.isPageLoaded[page] === true;
    }
    return isLoaded;
  };
  // }}}

  return [
    {
      state,
      listRef,
      infiniteLoaderRef,
      autosizerRef,
    },
    {
      dispatch,
      fetchTransactions,
      fetchTransactionsMeta,
      reloadList,
      clearList,
      isItemLoaded,
      onChangeFilter,
    },
  ];
};

export default useTransactionsList;
